"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import {
  MagnifyingGlass,
  Plus,
  FunnelSimple,
  PencilSimple,
  TrashSimple,
  ArrowClockwise,
  X,
  WarningCircle,
  SpinnerGap,
  PlusCircle,
  User,
  Eye,
  Wallet,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  Gear,
  Warning,
  DotsThreeOutlineVertical,
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  CheckCircle,
  Percent,
} from "phosphor-react"
import { motion, AnimatePresence } from "framer-motion"
import { DateRangePicker, SearchableSelect, DeleteModal } from "../../components"
import CreditAccountFormModal from "./CreditAccountFormModal"
import CreditAccountDetailsModal from "./CreditAccountDetailsModal"
import creditAccountsService from "../../services/creditAccountsService"
import creditAccountHistoryService from "../../services/creditAccountHistoryService"
import clientsService from "../../services/clientsService"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"

/* -------------------- small UI helpers (no logic change) -------------------- */
const cn = (...c) => c.filter(Boolean).join(" ")

const Card = ({ className, children }) => (
  <div
    className={cn(
      "rounded-xl border border-neutral-200 dark:border-neutral-900 bg-white dark:bg-neutral-950",
      className
    )}
  >
    {children}
  </div>
)

const SoftCard = ({ className, children }) => (
  <div
    className={cn(
      "rounded-xl border border-neutral-200 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/30",
      className
    )}
  >
    {children}
  </div>
)

const PrimaryButton = ({ className, children, ...props }) => (
  <button
    {...props}
    className={cn(
      "inline-flex items-center text-sm justify-center gap-2 h-10 px-4 rounded-xl",
      "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black",
      "hover:scale-[1.02] active:scale-[0.98] transition-transform",
      className
    )}
  >
    {children}
  </button>
)

const SecondaryButton = ({ className, children, ...props }) => (
  <button
    {...props}
    className={cn(
      "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl",
      "border border-neutral-200 dark:border-neutral-900",
      "bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white",
      "hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors",
      className
    )}
  >
    {children}
  </button>
)

const Chip = ({ className, children }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border",
      className
    )}
  >
    {children}
  </span>
)

const CreditAccounts = () => {
  const { checkAccess } = useAccessChecker()
  const { currentUser } = useCurrentUser()
  const navigate = useNavigate()

  // State management
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    status: "",
    user: "",
    balanceType: "", // ✅ NEW
  })

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 6,
    totalPages: 1,
    totalItems: 0,
  })

  // Modals state
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [accountFormOpen, setAccountFormOpen] = useState(false)
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const [done, setDone] = useState(false)

  // Additional state for one-account-per-client logic
  const [userOptions, setUserOptions] = useState([])
  const [currentUserAccount, setCurrentUserAccount] = useState(null)
  const [hasUserAccount, setHasUserAccount] = useState(false)
  const [txStats, setTxStats] = useState(null)
  const [txLoading, setTxLoading] = useState(false)
  const [stats, setStats] = useState({
    totalPositiveBalance: 0,
    totalNegativeBalance: 0,
    totalMinimumBalance: 0,
    availableSlots: 0,
  })

  // Track if filters are active
  const [filtersActive, setFiltersActive] = useState(false)

  const statusOptions = useMemo(
    () => [
      { id: 1, name: "All Statuses", value: "" },
      { id: 2, name: "Active", value: "active" },
      { id: 3, name: "Inactive", value: "inactive" },
      { id: 5, name: "Suspended", value: "suspended" },
      { id: 6, name: "Frozen", value: "frozen" },
      { id: 7, name: "Blacklisted", value: "blacklisted" },
    ],
    []
  )

  const balanceOptions = useMemo(
    () => [
      { id: 1, name: "All Balances", value: "" },
      { id: 2, name: "Negative Balance", value: "negative" },
      { id: 3, name: "Minimum Balance (< $500)", value: "minimum" },
      { id: 4, name: "Positive Balance (≥ $500)", value: "positive" },
    ],
    []
  )

  // Set page title in navbar
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: {
        title:
          currentUser?.role?.name === "Client"
            ? "My Account"
            : "Credit Account Management",
      },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [currentUser?.role?.name])

  // Check if any filters are active
  useEffect(() => {
    const hasActiveFilters =
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.status !== "" ||
      filters.user !== "" ||
      filters.balanceType !== "" // ✅ NEW

    setFiltersActive(hasActiveFilters)
  }, [searchQuery, dateRange, filters])

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      if (currentUser?.role?.name === "Client") {
        await getCurrentUserAccount()
      } else {
        await fetchAccounts()
        await fetchClients()
      }
      setLoading(false)
    }

    if (currentUser) {
      fetchData()
    }
  }, [done, pagination.currentPage, pagination.pageSize, currentUser])

  const fetchAccounts = async () => {
    try {
      if (currentUser?.role?.name === "Client" && hasUserAccount) {
        return
      }

      const payload = {
        ...filters,
        searchValue: searchQuery,
        dateRange,
        pagination: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
      }

      const response = await creditAccountsService.search(payload)
      const responseData = response.data

      if (responseData?.statusCode !== 200) return

      setAccounts(responseData.data)

      if (responseData.pagination) {
        setPagination({
          currentPage: responseData.pagination.currentPage,
          pageSize: responseData.pagination.pageSize,
          totalPages: responseData.pagination.totalPages,
          totalItems: responseData.pagination.totalItems,
        })
      }

      // ✅ correct backend stats
      if (responseData.stats) {
        setStats((prev) => ({
          ...prev,
          ...responseData.stats,
        }))
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error)
      toast.error(error.response?.data?.message || "Failed to load accounts")
    }
  }

  // Fetch clients for user selection
  const fetchClients = async () => {
    try {
      if (currentUser?.role?.name === "Client") {
        return
      }

      const response = await clientsService.searchClients()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        const clients = responseData.data.map((item) => ({
          id: item._id,
          name: item.reference || item.fullName,
          value: item._id,
          email: item.email,
        }))

        setUserOptions(clients)
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error)
      toast.error(error.response?.data?.message || "Failed to load clients")
    }
  }

  // Get current user's account (for client role)
  const getCurrentUserAccount = async () => {
    if (currentUser?.role?.name === "Client") {
      try {
        const response = await creditAccountsService.getUserAccount({})
        const responseData = response.data

        if (responseData && responseData.statusCode === 200) {
          setCurrentUserAccount(responseData.data)
          setHasUserAccount(true)
          setAccounts([responseData.data])
          // fetch stats + recent transactions in parallel
          fetchClientExtras()
        }
      } catch (error) {
        setCurrentUserAccount(null)
        setHasUserAccount(false)
        setAccounts([])
      }
    }
  }

  const fetchClientExtras = async () => {
    setTxLoading(true)
    try {
      const statsRes = await creditAccountHistoryService.getStats()
      if (statsRes.data?.statusCode === 200) setTxStats(statsRes.data.data)
    } catch {
      // non-critical — page still works without extras
    } finally {
      setTxLoading(false)
    }
  }

  // Handle filter changes
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))
  }, [])

  // Handle date range changes
  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range)
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      status: "",
      user: "",
      balanceType: "", // ✅ NEW
    })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }, [])

  // Apply filters
  const applyFilters = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
    fetchAccounts()
  }, [filters, searchQuery, dateRange, pagination.pageSize, pagination.currentPage])

  // Reset filters and refresh data
  const resetAndRefresh = useCallback(() => {
    clearFilters()
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
    setTimeout(() => {
      fetchAccounts()
    }, 0)
  }, [clearFilters, filters, searchQuery, dateRange, pagination.pageSize, pagination.currentPage])

  // Toggle dropdown for a specific account
  const toggleDropdown = useCallback(
    (id) => {
      setDropdownOpen((prev) => (prev === id ? null : id))
    },
    []
  )

  // Open account form modal for creating or editing
  const openAccountForm = useCallback((id = null, edit = false) => {
    setSelectedAccountId(id)
    setIsEditing(edit)
    setAccountFormOpen(true)
    setDropdownOpen(null)
  }, [])

  // Open account details modal
  const openAccountDetails = useCallback((id) => {
    setSelectedAccountId(id)
    setAccountDetailsOpen(true)
    setDropdownOpen(null)
  }, [])

  // Handle successful form submission
  const handleFormSuccess = useCallback(() => {
    setDone((p) => !p)
  }, [])

  // Format date for display
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }, [])

  // Get status badge color
  const getStatusBadgeColor = useCallback((status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "inactive":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      case "frozen":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
      case "suspended":
        return "bg-white text-red-700 border border-red-300 dark:bg-neutral-900 dark:text-red-400 dark:border-red-500"
      case "blacklisted":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      default:
        return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
    }
  }, [])

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    setPagination((prev) => ({
      ...prev,
      currentPage: newPage,
    }))
  }, [pagination.totalPages])

  // Handle page size change
  const handlePageSizeChange = useCallback((e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }))
  }, [])

  // Handle search submission
  const handleSearch = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
    fetchAccounts()
  }, [filters, searchQuery, dateRange, pagination.pageSize, pagination.currentPage])

  const handleAddBalance = useCallback((id, name, balance, currency, user) => {
    const params = new URLSearchParams({
      accountId: id,
      name,
      balance: String(balance ?? "0"),
      currency: currency || "USD",
      user: JSON.stringify(user || {}),
    })
    navigate(`/add-balance-credit?${params.toString()}`)
  }, [navigate])

  // Generate page numbers for pagination
  const getPageNumbers = useCallback(() => {
    const totalPages = pagination.totalPages
    const currentPage = pagination.currentPage

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, "...", totalPages]
    }

    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }

    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }, [pagination.totalPages, pagination.currentPage])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".account-dropdown")) {
        setDropdownOpen(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownOpen])

  // Check if balance is below minimum
  const isBalanceBelowMinimum = useCallback((balance) => {
    return Number.parseFloat(balance) < 500
  }, [])

  /* -------------------- CLIENT VIEW -------------------- */
  const renderClientView = () => {
    const acc = currentUserAccount
    const belowMin = acc ? isBalanceBelowMinimum(acc.balance) : false
    const negative = acc ? Number(acc.balance) < 0 : false
    const balance = Number(acc?.balance || 0)

    const balanceTone = negative ? "red" : belowMin ? "yellow" : "green"
    const toneMap = {
      red:    { text: "text-red-600 dark:text-red-400",       bg: "bg-red-50 dark:bg-red-900/10",       border: "border-red-100 dark:border-red-900/30"    },
      yellow: { text: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-50 dark:bg-yellow-900/10", border: "border-yellow-100 dark:border-yellow-900/30"},
      green:  { text: "text-emerald-600 dark:text-emerald-400",bg:"bg-emerald-50 dark:bg-emerald-900/10",border:"border-emerald-100 dark:border-emerald-900/30"},
    }
    const tone = toneMap[balanceTone]
    const holder = acc?.user?.reference || currentUser?.reference || acc?.user?.fullName || currentUser?.fullName || "—"

    const fmtMoney = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 text-start">
        <div className="w-full  mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {loading ? (
            <ClientSkeleton />
          ) : hasUserAccount && acc ? (
            <div className="flex flex-col gap-6">

              {/* ── Header ── */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-400 dark:text-neutral-500 font-medium mb-1">Credit Account</p>
                  <h1 className="text-xl font-[400] text-neutral-900 dark:text-white">{acc.account_name}</h1>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 font-mono">ID: {acc.account_id || acc._id}</p>
                </div>
                <span className={cn(
                  "self-start sm:self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border",
                  acc.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-900/40"
                    : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/40"
                )}>
                  <span className={cn("w-1.5 h-1.5 rounded-full", acc.status === "active" ? "bg-emerald-500" : "bg-red-500")} />
                  {acc.status.charAt(0).toUpperCase() + acc.status.slice(1)}
                </span>
              </div>

              {/* ── Alert ── */}
              {(negative || belowMin) && (
                <div className={cn("flex items-start gap-3 p-4 rounded-xl border",
                  negative ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/30"
                           : "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-900/30")}>
                  <Warning size={18} className={cn("mt-0.5 flex-shrink-0", negative ? "text-red-500" : "text-yellow-500")} />
                  <div>
                    <p className={cn("text-sm font-semibold", negative ? "text-red-800 dark:text-red-200" : "text-yellow-800 dark:text-yellow-200")}>
                      {negative ? "Negative balance detected" : "Balance below minimum"}
                    </p>
                    <p className={cn("text-xs mt-0.5", negative ? "text-red-700 dark:text-red-300" : "text-yellow-700 dark:text-yellow-300")}>
                      {negative ? "Your balance is negative. Please add funds to continue using your ad accounts."
                                : "Minimum deposit accepted is $500. Please top up your account."}
                    </p>
                  </div>
                </div>
              )}

              {/* ── Row 1: Balance hero + Account info ── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* Balance hero */}
                <div className={cn("lg:col-span-2 rounded-2xl border overflow-hidden", tone.border, tone.bg)}>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-widest font-semibold text-neutral-500 dark:text-neutral-400 mb-4">Available Balance</p>
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                      <div>
                        <div className={cn("text-5xl font-[300] tracking-tight tabular-nums", tone.text)}>
                          {balance < 0 ? "-" : ""}${fmtMoney(Math.abs(balance))}
                        </div>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">{acc.account_currency} · Credit Account</p>
                      </div>
                      <PrimaryButton
                        onClick={() => handleAddBalance(acc._id, acc.account_name, acc.balance, acc.account_currency, acc.user)}
                        className="h-11 px-6 self-start sm:self-end shrink-0"
                      >
                        <Plus size={17} /> Add Balance
                      </PrimaryButton>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-neutral-200 dark:divide-neutral-800 border-t border-neutral-200 dark:border-neutral-800 bg-white/60 dark:bg-neutral-950/60">
                    {[
                      { label: "Account Type", value: "Credit" },
                      { label: "Currency",      value: acc.account_currency },
                      { label: "Member Since",  value: formatDate(acc.createdAt) },
                    ].map(({ label, value }) => (
                      <div key={label} className="px-4 py-3.5">
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-semibold mb-0.5">{label}</p>
                        <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Account info + Actions */}
                <div className="flex flex-col gap-5">
                  <Card className="p-5">
                    <SectionLabel icon={<User size={13} />} label="Account Info" />
                    <div className="space-y-3.5 mt-4">
                      {[
                        { label: "Holder",       value: holder },
                        { label: "Created",      value: formatDate(acc.createdAt) },
                        { label: "Last Updated", value: formatDate(acc.updatedAt) },
                        { label: "Account ID",   value: acc.account_id || acc._id, mono: true },
                      ].map(({ label, value, mono }) => (
                        <div key={label} className="flex items-start justify-between gap-3">
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0 pt-px">{label}</p>
                          <p className={cn("text-xs text-right text-neutral-800 dark:text-neutral-100 font-medium break-all", mono && "font-mono")}>{value}</p>
                        </div>
                      ))}
                    </div>
                  </Card>

                  <Card className="p-5">
                    <SectionLabel icon={<Gear size={13} />} label="Quick Actions" />
                    <div className="space-y-2 mt-4">
                      <ActionRow icon={<PlusCircle size={15} className="text-blue-500" />} iconBg="bg-blue-50 dark:bg-blue-900/20" label="Add Balance" sub="Deposit funds to your account"
                        onClick={() => handleAddBalance(acc._id, acc.account_name, acc.balance, acc.account_currency, acc.user)} />
                      <ActionRow icon={<Eye size={15} className="text-emerald-500" />} iconBg="bg-emerald-50 dark:bg-emerald-900/20" label="View Full History" sub="All transactions & payments"
                        onClick={() => navigate("/credit-accounts-history")} />
                      <ActionRow icon={<Wallet size={15} className="text-violet-500" />} iconBg="bg-violet-50 dark:bg-violet-900/20" label="Account Details" sub="View account information"
                        onClick={() => openAccountDetails(acc._id)} />
                      {checkAccess("creditsAccounts", "update") && (
                        <ActionRow icon={<Gear size={15} className="text-purple-500" />} iconBg="bg-purple-50 dark:bg-purple-900/20" label="Account Settings" sub="Edit account info"
                          onClick={() => openAccountForm(acc._id, true)} />
                      )}
                    </div>
                  </Card>
                </div>
              </div>

      
         
            </div>

          ) : (
            <div className="flex flex-col items-center justify-center text-center py-24 gap-5">
              <div className="w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 flex items-center justify-center">
                <Wallet size={28} className="text-neutral-400 dark:text-neutral-500" />
              </div>
              <div>
                <h2 className="text-lg font-[400] text-neutral-900 dark:text-white">No credit account yet</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-sm">
                  Create a credit account to start funding your ad accounts.
                </p>
              </div>
              {checkAccess("creditsAccounts", "create") && (
                <PrimaryButton onClick={() => openAccountForm()} className="h-11 px-6">
                  <Plus size={17} /> Create Account
                </PrimaryButton>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  /* -------------------- ADMIN VIEW (same logic, cleaned structure) -------------------- */
  const renderAdminView = () => {
    return (
      <div className="lg:p-7 p-3 max-w-full mx-auto">
        {/* Stats */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start justify-center flex-col">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Positive Balance</p>
                  <p className="text-2xl font-normal text-green-700 dark:text-green-600">
                    {Number(stats.totalPositiveBalance || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}$
                  </p>
                </div>
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <Wallet className="w-6 h-6 text-green-700 dark:text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start justify-center flex-col">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Minimum Balance</p>
                  <p className="text-2xl font-normal text-yellow-600 dark:text-yellow-400">
                    {Number(stats.totalMinimumBalance || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}$
                  </p>
                </div>
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  <Wallet className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start justify-center flex-col">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Negative Balance</p>
                  <p className="text-2xl font-normal text-red-700 dark:text-red-600">
                    {Number(stats.totalNegativeBalance || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}$
                  </p>
                </div>
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <Wallet className="w-6 h-6 text-red-700 dark:text-red-600" />
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Top controls */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Search */}
          <div className="relative flex-grow max-w-xs">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
            </div>
            <input
              type="text"
              placeholder="Search accounts..."
              className={cn(
                "h-10 w-full border pl-10 pr-10 text-sm rounded-xl",
                "border-neutral-150 dark:border-neutral-900",
                "bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200",
                "placeholder-neutral-500 focus:outline-none focus:ring-1",
                "ring-neutral-300 dark:ring-neutral-700"
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch()
              }}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              onClick={handleSearch}
              aria-label="Submit search"
            >
              <ArrowRight size={16} />
            </button>
          </div>

          {/* Date */}
          <DateRangePicker
            onRangeChange={handleDateRangeChange}
            value={dateRange}
            className={
              dateRange.start || dateRange.end
                ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                : ""
            }
          />

          {/* More filters */}
          <button
            className={cn(
              "flex items-center gap-2 px-4 h-10 border text-sm rounded-xl focus:outline-none focus:ring-1",
              showMoreFilters
                ? "border-yellow-500 bg-yellow-50 text-yellow-600 dark:border-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400"
                : "border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            )}
            onClick={() => setShowMoreFilters(!showMoreFilters)}
          >
            <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
            {showMoreFilters ? (
              <X size={16} className="text-yellow-600 dark:text-yellow-400" />
            ) : (
              <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
            )}
          </button>

          <SecondaryButton onClick={resetAndRefresh} aria-label="Reset filters">
            <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
            <span className="text-sm">Reset</span>
          </SecondaryButton>

          {/* Apply when active */}
          {filtersActive && !showMoreFilters && (
            <PrimaryButton onClick={applyFilters}>
              <FunnelSimple size={16} />
              <span className="text-sm">Apply Filters</span>
            </PrimaryButton>
          )}

          {/* Add account */}
          {checkAccess("creditsAccounts", "create") && (
            <PrimaryButton onClick={() => openAccountForm()}>
              <Plus size={16} />
              <span className="text-sm">Add Account</span>
            </PrimaryButton>
          )}
        </div>

        {/* Active filters chips */}
        {filtersActive && (
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

            {searchQuery && (
              <div className="flex items-center gap-1 border-yellow-500/20 border bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full text-xs">
                <MagnifyingGlass size={12} />
                <span>{searchQuery}</span>
                <button
                  onClick={() => {
                    setSearchQuery("")
                    handleSearch()
                  }}
                  className="ml-1 hover:text-yellow-900 dark:hover:text-yellow-100"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {(dateRange.start || dateRange.end) && (
              <div className="flex items-center gap-1 border-yellow-500/20 border bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full text-xs">
                <CalendarBlank size={12} />
                <span>
                  {dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} -{" "}
                  {dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"}
                </span>
                <button
                  onClick={() => {
                    setDateRange({ start: "", end: "" })
                    handleSearch()
                  }}
                  className="ml-1 hover:text-yellow-900 dark:hover:text-yellow-100"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {filters.status && (
              <div className="flex items-center gap-1 border-yellow-500/20 border bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full text-xs">
                <WarningCircle size={12} />
                <span>{filters.status}</span>
                <button
                  onClick={() => {
                    handleFilterChange("status", "")
                    applyFilters()
                  }}
                  className="ml-1 hover:text-yellow-900 dark:hover:text-yellow-100"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {filters.balanceType && (
              <div className="flex items-center gap-1 border-yellow-500/20 border bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full text-xs">
                <Wallet size={12} />
                <span>
                  {balanceOptions.find((o) => o.value === filters.balanceType)?.name || filters.balanceType}
                </span>
                <button
                  onClick={() => {
                    handleFilterChange("balanceType", "")
                    applyFilters()
                  }}
                  className="ml-1 hover:text-yellow-900 dark:hover:text-yellow-100"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            <button
              onClick={resetAndRefresh}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
            >
              <X size={12} />
              <span>Clear all</span>
            </button>
          </div>
        )}

        {/* Expanded filters */}
        {showMoreFilters && (
          <Card className="mb-6 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
              <button
                onClick={clearFilters}
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
              >
                <X size={14} />
                <span>Clear all</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <SearchableSelect
                options={statusOptions}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                placeholder="Status"
                icon={<WarningCircle size={16} />}
              />

              {!["Client"].includes(currentUser?.role?.name) && (
                <SearchableSelect
                  options={userOptions}
                  value={filters.user}
                  onChange={(value) => handleFilterChange("user", value)}
                  placeholder="Client"
                  icon={<User size={16} />}
                />
              )}

              <SearchableSelect
                options={balanceOptions}
                value={filters.balanceType}
                onChange={(value) => handleFilterChange("balanceType", value)}
                placeholder="Balance"
                icon={<Wallet size={16} />}
              />

              <div className="flex items-center gap-2">
                <PrimaryButton className="h-9 px-4" onClick={applyFilters}>
                  <FunnelSimple size={15} />
                  <span className="text-sm">Apply Filters</span>
                </PrimaryButton>

                <SecondaryButton className="h-9 px-4" onClick={resetAndRefresh}>
                  <ArrowClockwise size={15} />
                  <span className="text-sm">Reset</span>
                </SecondaryButton>
              </div>
            </div>
          </Card>
        )}

        {/* Cards grid */}
        <div className="overflow-x-auto rounded-xl">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <SpinnerGap size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
            </div>
          ) : accounts.length > 0 ? (
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {accounts.map((account) => (
                  <motion.div
                    key={account._id}
                    className="border relative border-neutral-150 h-full dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 rounded-xl w-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Card header */}
                    <div className="flex relative justify-between items-center text-sm bg-gradient-to-r from-neutral-900/80 to-neutral-900 dark:from-neutral-900/40 dark:to-neutral-900 px-5 py-3 rounded-t-2xl border-b border-neutral-150 dark:border-neutral-900">
                      <div className="flex items-center">
                        <User size={14} weight="regular" className="mr-2 text-neutral-100" />
                        <span className="text-neutral-100 font-[400] text-[14px]">
                          {account?.user?.reference || account?.user?.fullName}
                        </span>
                      </div>

                      <div className="account-dropdown relative">
                        <button
                          onClick={() => toggleDropdown(account._id)}
                          className="p-1.5 rounded-full hover:bg-neutral-900 text-neutral-200"
                        >
                          <DotsThreeOutlineVertical size={16} weight="bold" />
                        </button>

                        <AnimatePresence>
                          {dropdownOpen === account._id && (
                            <motion.div
                              className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-950 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-900 py-1 z-20"
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              transition={{ duration: 0.1 }}
                            >
                              <button
                                onClick={() => openAccountDetails(account._id)}
                                className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                              >
                                <Eye size={16} className="mr-2" />
                                View Details
                              </button>

                              {checkAccess("creditsAccounts", "update") && (
                                <button
                                  onClick={() => openAccountForm(account._id, true)}
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                  <PencilSimple size={16} className="mr-2" />
                                  Edit Account
                                </button>
                              )}

                              {checkAccess("creditsAccounts", "delete") && (
                                <button
                                  onClick={() => {
                                    setDeleteId(account._id)
                                    setDeleteModal(true)
                                    setDropdownOpen(null)
                                  }}
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                  <TrashSimple size={16} className="mr-2" />
                                  Delete
                                </button>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm font-[300] text-neutral-600 dark:text-neutral-400">
                          Tracking Balance
                        </div>
                        <button
                          className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={() =>
                            handleAddBalance(
                              account._id,
                              account.account_name,
                              account.balance,
                              account.account_currency,
                              account.user
                            )
                          }
                        >
                          <PlusCircle size={20} />
                        </button>
                      </div>

                      <div
                        className={cn(
                          "text-2xl text-start font-[400] mb-8",
                          account.balance < 0
                            ? "text-red-700 dark:text-red-600"
                            : account.balance < 500
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-green-700 dark:text-green-600"
                        )}
                      >
                        {Number(account.balance || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}$
                        <span className="text-sm ml-1 text-neutral-500 dark:text-neutral-400">
                          {account.account_currency}
                        </span>
                      </div>

                      <div className="mb-1">
                        <div className="font-[300] text-neutral-800 dark:text-neutral-300 text-sm text-start">
                          {account.account_name}
                        </div>
                        <div className="text-sm font-[300] flex justify-between mt-1 text-neutral-500 dark:text-neutral-400 items-center">
                          <span>Credit Reference : {account.account_id}</span>
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs",
                              getStatusBadgeColor(account.status)
                            )}
                          >
                            <span
                              className={cn(
                                "w-1.5 h-1.5 mr-1 rounded-full",
                                account.status === "active" ? "bg-green-500" : "bg-red-500"
                              )}
                            />
                            {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 p-4 rounded-xl">
                <div className="flex items-center mb-4 sm:mb-0">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Showing {accounts.length} of {pagination.totalItems} accounts
                  </span>
                  <div className="ml-4">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 px-2 text-sm border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white"
                    >
                      <option value={6}>6 per page</option>
                      <option value={12}>12 per page</option>
                      <option value={24}>24 per page</option>
                      <option value={48}>48 per page</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.currentPage === 1}
                    className={cn(
                      "p-1.5 rounded-xl",
                      pagination.currentPage === 1
                        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                    aria-label="First page"
                  >
                    <CaretDoubleLeft size={16} />
                  </button>

                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className={cn(
                      "p-1.5 rounded-xl",
                      pagination.currentPage === 1
                        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                    aria-label="Previous page"
                  >
                    <CaretLeft size={16} />
                  </button>

                  <div className="flex items-center space-x-1">
                    {getPageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={cn(
                            "w-8 h-8 flex items-center justify-center rounded-xl text-sm",
                            pagination.currentPage === page
                              ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black hover:scale-105 transition-all duration-400"
                              : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          )}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={cn(
                      "p-1.5 rounded-xl",
                      pagination.currentPage === pagination.totalPages
                        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                    aria-label="Next page"
                  >
                    <CaretRight size={16} />
                  </button>

                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={cn(
                      "p-1.5 rounded-xl",
                      pagination.currentPage === pagination.totalPages
                        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                    aria-label="Last page"
                  >
                    <CaretDoubleRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
              <div className="p-3 rounded-xl bg-neutral-100 dark:bg-neutral-900/40 border border-neutral-200 dark:border-neutral-900 flex items-center justify-center mb-4">
                <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
              </div>
              <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">
                No Credit Accounts Found
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
                {filtersActive
                  ? "No credit accounts match your search criteria. Try adjusting your filters or search term."
                  : "There are no credit accounts in the system yet. Each client can have only one account."}
              </p>
              <div className="flex gap-4">
                {filtersActive && (
                  <SecondaryButton onClick={resetAndRefresh}>
                    <ArrowClockwise size={16} />
                    Reset Filters
                  </SecondaryButton>
                )}
                {checkAccess("creditsAccounts", "create") && (
                  <PrimaryButton onClick={() => openAccountForm()}>
                    <Plus size={16} />
                    <span>Add Credit Account</span>
                  </PrimaryButton>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      {currentUser?.role?.name === "Client" ? renderClientView() : renderAdminView()}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="credit-accounts"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />

      {/* Credit Account Form Modal (Add/Edit) */}
      <CreditAccountFormModal
        isOpen={accountFormOpen}
        onClose={() => setAccountFormOpen(false)}
        accountId={selectedAccountId}
        isEditing={isEditing}
        onSuccess={handleFormSuccess}
      />

      {/* Credit Account Details Modal */}
      <CreditAccountDetailsModal
        isOpen={accountDetailsOpen}
        onClose={() => setAccountDetailsOpen(false)}
        accountId={selectedAccountId}
        onEdit={(id) => {
          setAccountDetailsOpen(false)
          openAccountForm(id, true)
        }}
      />
    </>
  )
}

export default CreditAccounts

/* ── Small helpers for the client view ────────────────────────────────────── */

function SectionLabel({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400">
      {icon}
      <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
    </div>
  )
}

function ActionRow({ icon, iconBg, label, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-xl text-left",
        "border border-neutral-100 dark:border-neutral-800",
        "hover:bg-neutral-50 dark:hover:bg-neutral-900/60 transition-colors"
      )}
    >
      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", iconBg)}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 leading-tight">{label}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">{sub}</p>
      </div>
      <ArrowRight size={14} className="ml-auto text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
    </button>
  )
}

function ClientSkeleton() {
  return (
    <div className="flex flex-col gap-6 animate-pulse">
      {/* title row */}
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <div className="h-2.5 w-24 bg-neutral-100 dark:bg-neutral-800 rounded-md" />
          <div className="h-5 w-48 bg-neutral-100 dark:bg-neutral-800 rounded-md" />
          <div className="h-2.5 w-32 bg-neutral-100 dark:bg-neutral-800 rounded-md" />
        </div>
        <div className="h-7 w-20 bg-neutral-100 dark:bg-neutral-800 rounded-full" />
      </div>
      {/* balance + info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-neutral-100 dark:bg-neutral-800 h-52" />
        <div className="space-y-5">
          <div className="rounded-xl bg-neutral-100 dark:bg-neutral-800 h-44" />
          <div className="rounded-xl bg-neutral-100 dark:bg-neutral-800 h-40" />
        </div>
      </div>
      {/* stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-neutral-100 dark:bg-neutral-800 h-20" />
        ))}
      </div>
      {/* fee rates */}
      <div className="rounded-xl bg-neutral-100 dark:bg-neutral-800 h-36" />
    </div>
  )
}